import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '../shared/services/auth.service';
import { User } from '../shared/models/user.model';
import { TranslatePipe } from '../shared/i18n/translate.pipe';
import { I18nService } from 'src/app/shared/i18n/i18n.service';

@Component({
    selector: 'app-signin-page',
    templateUrl: './signin-page.component.html',
    styleUrls: ['./signin-page.component.css'],
    standalone: true,
    imports: [ReactiveFormsModule, MatButtonModule, MatInputModule, RouterLink, TranslatePipe]
})
export class SigninPageComponent implements OnInit {

  emailFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.email,
  ]);

  passwordFormControl = new UntypedFormControl('', [
    Validators.required,
  ]);

  // The group makes the FormGroupDirective handle the submit event
  // (preventing the browser's native form submission / page reload)
  signinForm = new UntypedFormGroup({
    email: this.emailFormControl,
    password: this.passwordFormControl,
  });

  facebookAvailable = false;
  /** Komunikat po nieudanym powrocie z logowania Facebookiem */
  facebookError = '';

  constructor(private authService: AuthService, private router: Router, private route: ActivatedRoute,
    private i18n: I18nService) { }

  ngOnInit() {
    // Zalogowany użytkownik nie ma czego szukać na /signin - ale tylko wtedy,
    // gdy faktycznie zmierzamy na /signin. Komponent bywa odtwarzany w trakcie
    // nawigacji POWROTNEJ po zalogowaniu (AppComponent przełącza layout na
    // RoutesRecognized, zanim nawigacja się zakończy) i bezwarunkowe
    // przekierowanie anulowałoby powrót na stronę sprzed logowania.
    const navigation = this.router.getCurrentNavigation();
    const targetUrl = navigation
      ? (navigation.finalUrl ?? navigation.extractedUrl)?.toString()
      : this.router.url;
    if (this.authService.isLoggedIn() && (!targetUrl || targetUrl.startsWith('/signin'))) {
      this.router.navigate(['/']);
    }

    this.authService.getAuthProviders().subscribe({
      next: (providers) => this.facebookAvailable = !!providers?.facebook,
      error: () => this.facebookAvailable = false
    });

    this.facebookError = this.facebookErrorMessage(this.route.snapshot.queryParamMap.get('error'));
  }

  /** Kody błędów ustawia backend przy powrocie z OAuth */
  private facebookErrorMessage(code: string | null): string {
    switch (code) {
      case 'facebook_cancelled':
        return this.i18n.t('signin.fbCancelled');
      case 'facebook_noemail':
        return this.i18n.t('signin.fbNoEmail');
      case 'facebook_unconfirmed':
        return this.i18n.t('signin.fbUnconfirmed');
      case 'facebook_disabled':
        return this.i18n.t('signin.fbUnavailable');
      case 'facebook_state':
      case 'facebook_failed':
        return this.i18n.t('signin.fbFailed');
      default:
        return '';
    }
  }

  onAuthenticate() {
    const user = new User();
    user.email = this.emailFormControl.value;
    user.password = this.passwordFormControl.value;

    this.authService.signin(user).subscribe(
      data => {
        if (data.username) {
          localStorage.setItem('username', data.username);
          localStorage.setItem('userid', data.userId);
          localStorage.setItem('expires', data.expires);
          // Notify subscribers that auth state has changed
          this.authService.authStateChanged.next(true);
          const redirectUrl = this.authService.getRedirectUrl();
          if (redirectUrl) {
            // Powrót na stronę, z której użytkownik trafił do logowania
            // (ręczne "Zaloguj" na podstronie albo wygaśnięcie sesji)
            this.authService.clearRedirectUrl();
            this.router.navigateByUrl(redirectUrl);
          } else if (data.url) {
            this.router.navigateByUrl('/tree/' + data.url);
          } else {
            // Konto bez domyślnego drzewa (np. świeżo założone) - strona
            // główna prowadzi dalej do utworzenia drzewa. Wcześniej szło
            // tu '/tree/null' i użytkownik lądował na pustym ekranie.
            this.router.navigateByUrl('/');
          }
        }
      },
      error => {
      }
    );
  }

  onAuthenticateFacebook() {
    this.authService.signinFacebook(this.authService.getRedirectUrl() ?? '/');
  }
}
